import { Trash } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { SortableList } from "@/components/ui/auto-form/fields/sortable";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useArtistRepository } from "@/pages/artists/hooks/use-artist-repository";
import type { ArtistNestedType } from "@/pages/artists/schemas/artist.nested.form";
import type { FormEntry } from "@/pages/tina/festivals/lib/page-form";

/** Up to two initials of the name's words that start with a letter or a digit: "Renee Woodward" is "RW". */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .map((word) => Array.from(word).find((c) => /[\p{L}\p{N}]/u.test(c)))
    .filter((c): c is string => c !== undefined)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

interface DayLineupProps {
  dayKey: string;
  lineup: FormEntry[];
  editable: boolean;
  onChange: (lineup: FormEntry[]) => void;
  onAdd: () => void;
}

/** What the form keeps of a search hit: the picked artist's photo, as the event form shows it. */
const photoOf = (artist: ArtistNestedType): string | null =>
  artist.photo?.url ?? artist.media?.photos?.[0]?.url ?? null;

/**
 * One day's lineup, edited with the event form's parts: a sortable list of artist pickers (a Mood artist search, an
 * amber border while the row is only typed text), each with a photo or initials, a set time and a stage.
 */
export function DayLineup({
  dayKey,
  lineup,
  editable,
  onChange,
  onAdd,
}: DayLineupProps) {
  const { search } = useArtistRepository();

  const setEntry = (key: string, change: Partial<FormEntry>) =>
    onChange(lineup.map((e) => (e.key === key ? { ...e, ...change } : e)));

  if (!editable) {
    return (
      <ul className="flex flex-col gap-1" data-testid={`lineup-${dayKey}`}>
        {lineup.map((entry) => (
          <li key={entry.key} className="flex items-center gap-2 text-sm">
            <Avatar className="h-8 w-8">
              {entry.photoUrl ? (
                <AvatarImage src={entry.photoUrl} alt="" />
              ) : null}
              <AvatarFallback className="text-xs">
                {initials(entry.name)}
              </AvatarFallback>
            </Avatar>
            <span>{entry.name}</span>
            {[entry.setTime, entry.stage].filter(Boolean).length > 0 ? (
              <span className="text-xs text-muted-foreground">
                {[entry.setTime, entry.stage].filter(Boolean).join(" · ")}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      <SortableList
        handle
        orientation="vertical"
        items={lineup.map((entry) => ({ ...entry, id: entry.key }))}
        onChanged={(items: Array<FormEntry & { id: string }>) =>
          onChange(
            items
              .map((item) => lineup.find((e) => e.key === item.id))
              .filter((e): e is FormEntry => e !== undefined),
          )
        }
        render={(item: FormEntry) => {
          const label = item.name || "artist";
          return (
            <div className="flex w-full flex-col gap-1">
              <div className="flex w-full flex-row items-center gap-2">
                <Avatar className="h-10 w-10">
                  {item.photoUrl ? (
                    <AvatarImage src={item.photoUrl} alt="" />
                  ) : null}
                  <AvatarFallback className="text-xs">
                    {initials(item.name)}
                  </AvatarFallback>
                </Avatar>
                <RemoteSelect<ArtistNestedType>
                  parentClassName="min-w-0 flex-1"
                  className={`w-full min-w-0 overflow-hidden ${item.artistId === null ? "border-amber-400" : ""}`}
                  triggerType="change"
                  showSelected
                  placeholder="Search Mood artists"
                  value={
                    item.name === ""
                      ? undefined
                      : ({
                          id: item.artistId,
                          name: item.name,
                        } as ArtistNestedType)
                  }
                  defaultOpen={item.name === "" && item.artistId === null}
                  onValueChanged={(selected) =>
                    setEntry(
                      item.key,
                      selected === undefined
                        ? { name: "", artistId: null, photoUrl: null }
                        : {
                            name: selected.name,
                            artistId: selected.id ?? null,
                            photoUrl: photoOf(selected),
                          },
                    )
                  }
                  accessorFn={(option) => ({
                    id: option?.id || option?.name,
                    name: option ? `${option.name}` : "",
                    iconUrl: option?.media?.photos?.[0]?.url,
                  })}
                  onCreate={(query) => ({ name: query }) as ArtistNestedType}
                  onSearch={async (query, onStatusChanged) => {
                    onStatusChanged("loading");
                    const result = await search(query);
                    onStatusChanged("success");
                    return result;
                  }}
                />
                {/* IMS's Input wraps itself in a full-width box, so the width goes on a wrapper, else the two inputs take
                    the whole row and squeeze the artist picker to nothing. */}
                <div className="w-28 shrink-0">
                  <Input
                    type="time"
                    aria-label={`Set time of ${label}`}
                    value={item.setTime}
                    onChange={(event) =>
                      setEntry(item.key, { setTime: event.target.value })
                    }
                  />
                </div>
                <div className="w-32 shrink-0">
                  <Input
                    placeholder="Stage"
                    aria-label={`Stage of ${label}`}
                    value={item.stage}
                    onChange={(event) =>
                      setEntry(item.key, { stage: event.target.value })
                    }
                  />
                </div>
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className="shrink-0"
                  aria-label={`Remove ${label}`}
                  onClick={() =>
                    onChange(lineup.filter((e) => e.key !== item.key))
                  }
                >
                  <Trash className="size-4" />
                </Button>
              </div>
              {item.artistId === null && item.name !== "" ? (
                <p className="pl-12 text-xs text-amber-700">
                  Not linked to a Mood artist. Tina looks it up when it
                  publishes.
                </p>
              ) : null}
            </div>
          );
        }}
      />
      <Button
        type="button"
        variant="secondary"
        className="self-start"
        onClick={onAdd}
      >
        + Add artist (search Mood artists)
      </Button>
    </div>
  );
}
